import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Check,
  CircleDashed,
  Copy,
  Loader2,
  MinusCircle,
  Plus,
  Radar,
  RefreshCw,
  X,
} from 'lucide-react';
import {
  OLT_PLATFORM_LABELS,
  OLT_SETUP_STEPS,
  parseNtpServers,
  type CommandBlock,
  type JobHandle,
  type JobStatus,
  type NtpSettings,
  type Olt,
  type OltPlatform,
  type OltSetupResult,
  type OltSetupStep,
  type OltSetupStepId,
  type Organization,
  type Page,
  type PreflightCheck,
  type PrepPlan,
  type Site,
} from '@simpleacs/shared';
import { api } from '../lib/api';
import { useSession } from '../lib/session';
import {
  generateOltCommunity,
  generateOltPassword,
  isEmptyDraft,
  newVlanDraft,
  toOnboardingVlans,
  vlanDraftProblems,
  type VlanDraft,
} from '../lib/olt-onboarding';
import { TimezoneSelect } from '../components/TimezoneSelect';
import { Button, Card, ErrorNotice, Field, Input, Pill, Spinner } from '../components/ui';

type StepId = 'platform' | 'network' | 'prepare' | 'verify' | 'setup';

const STEPS: Array<{ id: StepId; label: string; blurb: string }> = [
  { id: 'platform', label: 'Platform', blurb: 'Which terminal, and where it lives' },
  { id: 'network', label: 'Addressing', blurb: 'Credentials and VLANs' },
  { id: 'prepare', label: 'Prepare', blurb: 'What to type on the OLT' },
  { id: 'verify', label: 'Verify', blurb: 'The login works' },
  { id: 'setup', label: 'Set up', blurb: 'SimpleACS finishes the rest' },
];

interface WizardState {
  platform: OltPlatform;
  name: string;
  mgmtIp: string;
  mgmtNetmask: string;
  mgmtGateway: string;
  managementMode: 'out_of_band' | 'in_band';
  mgmtVlan: string;
  uplinkPort: string;
  cliUsername: string;
  cliPassword: string;
  cliTransport: 'ssh' | 'telnet';
  cliPort: string;
  snmpVersion: '1' | '2c' | '3';
  snmpCommunity: string;
  snmpCommunityRw: string;
  snmpPort: string;
  siteId: string;
  vlans: VlanDraft[];
  ntpServers: string;
  ntpInterval: string;
  timezone: string | null;
  eventNotifications: boolean;
  orgId: string;
}

/**
 * Where a new wizard starts. The password and both communities are GENERATED
 * (operator, 2026-09-25: "set autogenerate community strings", "autogenerate
 * simpleacs password", "set snmp read/write because we now do that") — fresh
 * for every wizard, never a fixed `simpleacs-ro`.
 */
function initialState(): WizardState {
  return {
    platform: 'zte-c3xx',
    name: '',
    mgmtIp: '',
    mgmtNetmask: '255.255.255.0',
    mgmtGateway: '',
    managementMode: 'out_of_band',
    mgmtVlan: '300',
    uplinkPort: '',
    cliUsername: 'simpleacs',
    cliPassword: generateOltPassword(),
    cliTransport: 'telnet',
    cliPort: '23',
    snmpVersion: '2c',
    snmpCommunity: generateOltCommunity('ro'),
    snmpCommunityRw: generateOltCommunity('rw'),
    snmpPort: '161',
    siteId: '',
    vlans: [newVlanDraft()],
    /**
     * The fallback, replaced by the platform's own set the moment Settings
     * answers — see the effect below. Two public stratum-1 pools, which is what
     * the operator's own chassis are already pointed at.
     */
    ntpServers: 'time.google.com\ntime.cloudflare.com',
    ntpInterval: '',
    timezone: null,
    /**
     * On, like every OLT already registered. Offered for the chassis whose
     * chatter is known about in advance — not an opt-in.
     */
    eventNotifications: true,
    orgId: '',
  };
}

/**
 * The poll interval means different things per vendor, so it is labelled as
 * what it is: ZTE takes a power-of-two exponent, Huawei takes seconds.
 */
function ntpIntervalLabel(platform: OltPlatform): string {
  return platform.startsWith('huawei') ? 'Sync interval (seconds)' : 'Poll interval (exponent)';
}

function ntpIntervalHint(platform: OltPlatform): string {
  return platform.startsWith('huawei')
    ? 'Optional. 64–1024; the chassis default is 600.'
    : 'Optional. 4–17, a power-of-two exponent — 10 is about 17 minutes.';
}

/** Only the fields the API's prep/preflight schemas accept. */
function toPrepInput(state: WizardState) {
  return {
    platform: state.platform,
    name: state.name.trim() || 'olt',
    mgmtIp: state.mgmtIp.trim(),
    mgmtNetmask: state.mgmtNetmask.trim(),
    mgmtGateway: state.mgmtGateway.trim() || '0.0.0.0',
    managementMode: state.managementMode,
    ...(state.managementMode === 'in_band' ? { mgmtVlan: Number(state.mgmtVlan) } : {}),
    ...(state.uplinkPort ? { uplinkPort: state.uplinkPort } : {}),
    cliUsername: state.cliUsername,
    ...(state.cliPassword ? { cliPassword: state.cliPassword } : {}),
    cliTransport: state.cliTransport,
    snmpVersion: state.snmpVersion,
    snmpCommunity: state.snmpCommunity,
    snmpCommunityRw: state.snmpCommunityRw,
    ...(state.siteId ? { siteId: state.siteId } : {}),
    vlans: toOnboardingVlans(state.vlans),
    ...(parseNtpServers(state.ntpServers).length > 0
      ? { ntpServers: parseNtpServers(state.ntpServers) }
      : {}),
    ...(state.ntpInterval ? { ntpInterval: Number(state.ntpInterval) } : {}),
    // Recorded on the OLT and never written by the wizard: the timezone
    // command form is unconfirmed for both vendors.
    ...(state.timezone ? { timezone: state.timezone } : {}),
  };
}

export function OltOnboardingPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { session, activeOrg } = useSession();
  const needsOrgChoice = session?.user.role === 'superadmin' && !activeOrg;

  const [stepIndex, setStepIndex] = useState(0);
  const [state, setState] = useState<WizardState>(initialState);
  const step = STEPS[stepIndex]!;

  const set = (patch: Partial<WizardState>) => setState((prev) => ({ ...prev, ...patch }));

  const sitesQuery = useQuery({
    queryKey: ['sites', activeOrg?.id ?? 'all'],
    queryFn: () => api.get<Page<Site>>('/sites'),
  });
  const orgsQuery = useQuery({
    queryKey: ['orgs'],
    queryFn: () => api.get<Page<Organization>>('/orgs'),
    enabled: needsOrgChoice,
  });

  /**
   * The platform's own time servers, so nobody has to remember them here.
   * Applied only while the operator has not touched the field — a prefill that
   * overwrites typing is worse than none.
   */
  const ntpDefaults = useQuery({
    queryKey: ['settings', 'ntp'],
    queryFn: () => api.get<{ effective: NtpSettings }>('/settings/ntp'),
  });
  const defaultsApplied = useRef(false);
  useEffect(() => {
    const effective = ntpDefaults.data?.effective;
    if (!effective || defaultsApplied.current) return;
    defaultsApplied.current = true;
    if (effective.servers.length === 0 && !effective.timezone) return;
    setState((prev) => ({
      ...prev,
      ...(effective.servers.length > 0 ? { ntpServers: effective.servers.join('\n') } : {}),
      ...(effective.timezone ? { timezone: effective.timezone } : {}),
    }));
  }, [ntpDefaults.data]);

  const plan = useMutation({
    mutationFn: () => api.post<PrepPlan>('/olts/prep-plan', toPrepInput(state)),
  });

  const preflight = useMutation({
    mutationFn: () =>
      api.post<{ ready: boolean; checks: PreflightCheck[]; detectedModel: string | null }>(
        '/olts/preflight',
        { ...toPrepInput(state), snmpPort: Number(state.snmpPort), cliPort: Number(state.cliPort) },
      ),
  });

  // --- the setup, once the OLT is registered -----------------------------------------
  const [onboarded, setOnboarded] = useState<Olt | null>(null);
  const [setupJobId, setSetupJobId] = useState<string | null>(null);

  const onboard = useMutation({
    mutationFn: () =>
      api.post<{ olt: Olt; setup: JobHandle | null }>('/olts/onboard', {
        ...toPrepInput(state),
        snmpPort: Number(state.snmpPort),
        cliPort: Number(state.cliPort),
        // Only on the onboard call: a display preference has no business in
        // prep-plan or preflight, which are about commands for a chassis.
        eventNotifications: state.eventNotifications,
        ...(needsOrgChoice && state.orgId ? { orgId: state.orgId } : {}),
      }),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ['olts'] });
      setOnboarded(result.olt);
      setSetupJobId(result.setup?.id ?? null);
    },
  });

  /** The same setup again, on the registered OLT — safe: it skips what is there. */
  const rerun = useMutation({
    mutationFn: (olt: Olt) =>
      api.post<JobHandle>(`/olts/${encodeURIComponent(olt.id)}/setup`, {
        vlans: toOnboardingVlans(state.vlans),
        platform: state.platform,
      }),
    onSuccess: (handle) => setSetupJobId(handle.id),
  });

  const job = useQuery({
    queryKey: ['job', setupJobId],
    queryFn: () => api.get<JobStatus>(`/jobs/${encodeURIComponent(setupJobId!)}`),
    enabled: setupJobId !== null,
    refetchInterval: (q) => {
      const status = q.state.data?.status;
      return !status || status === 'running' || status === 'queued' ? 1500 : false;
    },
  });

  const vlanProblems = useMemo(() => vlanDraftProblems(state.vlans), [state.vlans]);

  const canLeavePlatform = state.name.trim().length >= 2 && (!needsOrgChoice || Boolean(state.orgId));
  const canLeaveNetwork =
    state.mgmtIp.trim().length >= 7 &&
    state.mgmtGateway.trim().length >= 7 &&
    state.cliPassword.length > 0 &&
    state.snmpCommunity.trim().length > 0 &&
    vlanProblems.size === 0;

  function goNext() {
    const next = stepIndex + 1;
    if (STEPS[next]?.id === 'prepare') plan.mutate();
    if (STEPS[next]?.id === 'verify') preflight.mutate();
    setStepIndex(next);
  }

  // Once registered, the steps before are history: going back would offer to
  // register the same chassis twice.
  const locked = onboarded !== null;

  return (
    <>
      <button
        onClick={() => navigate('/olts')}
        className="mb-4 inline-flex items-center gap-2 text-sm text-muted hover:text-ink"
      >
        <ArrowLeft className="size-4" />
        Back to OLTs
      </button>

      <div className="mb-6">
        <h1 className="text-xl font-semibold text-ink">Onboard an OLT</h1>
        <p className="mt-1 text-sm text-muted">
          Prepare the terminal up to its login; SimpleACS checks the login and sets the rest up
          itself.
        </p>
      </div>

      {/* Stepper */}
      <ol className="mb-6 flex flex-wrap gap-2">
        {STEPS.map((entry, index) => {
          const done = index < stepIndex;
          const current = index === stepIndex;
          return (
            <li
              key={entry.id}
              className={`flex min-w-40 flex-1 items-center gap-3 rounded-xl border px-4 py-3 ${
                current
                  ? 'border-primary/40 bg-primary/8'
                  : done
                    ? 'border-line bg-surface-2'
                    : 'border-line bg-surface'
              }`}
            >
              <span
                className={`flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
                  done
                    ? 'bg-success/15 text-success'
                    : current
                      ? 'bg-primary text-white'
                      : 'bg-surface-3 text-faint'
                }`}
              >
                {done ? <Check className="size-3.5" /> : index + 1}
              </span>
              <span className="min-w-0">
                <span className={`block text-sm ${current ? 'text-ink' : 'text-muted'}`}>
                  {entry.label}
                </span>
                <span className="block truncate text-xs text-faint">{entry.blurb}</span>
              </span>
            </li>
          );
        })}
      </ol>

      {step.id === 'platform' && (
        <Card title="What are you onboarding?">
          <div className="grid gap-4 p-5 md:grid-cols-2">
            <Field label="Platform" hint="The command set differs within a vendor">
              <select
                className="input"
                value={state.platform}
                onChange={(e) => set({ platform: e.target.value as OltPlatform })}
              >
                {(Object.keys(OLT_PLATFORM_LABELS) as OltPlatform[]).map((platform) => (
                  <option key={platform} value={platform}>
                    {OLT_PLATFORM_LABELS[platform]}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Name" hint="How operators will refer to it">
              <Input
                value={state.name}
                onChange={(e) => set({ name: e.target.value })}
                placeholder="gachie1"
                autoFocus
              />
            </Field>
            {needsOrgChoice && (
              <Field label="Organization">
                <select
                  className="input"
                  value={state.orgId}
                  onChange={(e) => set({ orgId: e.target.value })}
                >
                  <option value="">Select…</option>
                  {orgsQuery.data?.items.map((org) => (
                    <option key={org.id} value={org.id}>
                      {org.name}
                    </option>
                  ))}
                </select>
              </Field>
            )}
            {/*
              A display preference, asked beside the name because that is what
              it is about: how this chassis appears in the fleet list.
            */}
            <label className="flex cursor-pointer items-start gap-2.5 rounded-lg border border-line bg-surface-2 px-3 py-2.5 md:col-span-2">
              <input
                type="checkbox"
                className="mt-0.5 size-4 accent-primary"
                checked={state.eventNotifications}
                onChange={(e) => set({ eventNotifications: e.target.checked })}
              />
              <span className="text-xs text-muted">
                <span className="block text-sm text-ink">
                  Show event notifications for this OLT on the OLT list
                </span>
                Off hides its unseen-event count and the warning triangle beside its name. The
                events are still recorded and still listed on the OLT&rsquo;s own Events tab, and
                genuine alarms — unreachable chassis, failed backup, optical findings — still reach
                the notification bell either way. Changeable later from Edit.
              </span>
            </label>
          </div>
        </Card>
      )}

      {step.id === 'network' && (
        <div className="space-y-5">
          <Card title="How this platform reaches the OLT">
            <div className="grid gap-4 p-5 md:grid-cols-2 xl:grid-cols-3">
              <Field label="Management address" hint="What the OLT will answer on">
                <Input
                  value={state.mgmtIp}
                  onChange={(e) => set({ mgmtIp: e.target.value })}
                  placeholder="10.200.0.2"
                />
              </Field>
              <Field label="Netmask">
                <Input
                  value={state.mgmtNetmask}
                  onChange={(e) => set({ mgmtNetmask: e.target.value })}
                />
              </Field>
              <Field label="Default gateway">
                <Input
                  value={state.mgmtGateway}
                  onChange={(e) => set({ mgmtGateway: e.target.value })}
                  placeholder="10.200.0.1"
                />
              </Field>
              <Field label="Management port">
                <select
                  className="input"
                  value={state.managementMode}
                  onChange={(e) =>
                    set({ managementMode: e.target.value as WizardState['managementMode'] })
                  }
                >
                  <option value="out_of_band">Out-of-band (dedicated MGMT port)</option>
                  <option value="in_band">In-band (VLAN on the uplink)</option>
                </select>
              </Field>
              {state.managementMode === 'in_band' && (
                <>
                  <Field label="OLT management VLAN">
                    <Input
                      value={state.mgmtVlan}
                      onChange={(e) => set({ mgmtVlan: e.target.value })}
                    />
                  </Field>
                  <Field label="Uplink port" hint="Where the VLAN is tagged">
                    <Input
                      value={state.uplinkPort}
                      onChange={(e) => set({ uplinkPort: e.target.value })}
                      placeholder="xgei-1/19/1"
                    />
                  </Field>
                </>
              )}
              <Field label="Reached through gateway" hint="Leave as direct if it is routable already">
                <select
                  className="input"
                  value={state.siteId}
                  onChange={(e) => set({ siteId: e.target.value })}
                >
                  <option value="">Directly routable</option>
                  {sitesQuery.data?.items.map((site) => (
                    <option key={site.id} value={site.id}>
                      {site.name}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
          </Card>

          <CredentialsCard state={state} set={set} />

          <VlansCard
            rows={state.vlans}
            problems={vlanProblems}
            gatewayName={sitesQuery.data?.items.find((s) => s.id === state.siteId)?.name ?? null}
            onChange={(vlans) => set({ vlans })}
          />

          {/* Time belongs on this step: a chassis reaches its NTP servers over
              the same management network being set up here. */}
          <Card title="Time">
            <div className="grid gap-4 p-5 md:grid-cols-2">
              <p className="text-sm text-muted md:col-span-2">
                A chassis with no time source keeps a clock that looks right today. The cost
                arrives months later, when every event and optical reading it recorded carries the
                wrong timestamp. SimpleACS adds these during setup, next to any the chassis
                already asks.
              </p>
              <Field
                label="NTP servers"
                hint="One per line, or comma-separated. A name like time.google.com is fine — no OLT accepts one, so SimpleACS resolves it and sends the address."
              >
                <textarea
                  className="input min-h-[5rem] font-mono text-xs"
                  value={state.ntpServers}
                  onChange={(e) => set({ ntpServers: e.target.value })}
                  placeholder="time.google.com"
                />
              </Field>
              <Field
                label={ntpIntervalLabel(state.platform)}
                hint={ntpIntervalHint(state.platform)}
              >
                <Input
                  value={state.ntpInterval}
                  onChange={(e) => set({ ntpInterval: e.target.value })}
                  placeholder={state.platform.startsWith('huawei') ? '600' : '10'}
                />
              </Field>
              {/* Recorded on the OLT, deliberately not written by the wizard:
                  the timezone command form has not been read back off either
                  vendor's hardware. */}
              <Field
                label="Timezone"
                hint="Recorded on the OLT. Writing it to the chassis is a separate, confirmed step on the OLT's Time card."
              >
                <TimezoneSelect value={state.timezone} onChange={(timezone) => set({ timezone })} />
              </Field>
              {ntpDefaults.data?.effective.servers.length ? (
                <p className="text-xs text-faint md:col-span-2">
                  Prefilled from Settings → NTP servers. Changing it here affects only this OLT.
                </p>
              ) : null}
            </div>
          </Card>
        </div>
      )}

      {step.id === 'prepare' && (
        <PrepareStep plan={plan.data} error={plan.error} loading={plan.isPending} />
      )}

      {step.id === 'verify' && (
        <VerifyStep
          result={preflight.data}
          error={preflight.error}
          loading={preflight.isPending}
          onRetry={() => preflight.mutate()}
        />
      )}

      {step.id === 'setup' && (
        <SetupStep
          state={state}
          gatewayName={sitesQuery.data?.items.find((s) => s.id === state.siteId)?.name ?? null}
          detectedModel={preflight.data?.detectedModel ?? null}
          onboarded={onboarded}
          onboardError={onboard.error}
          job={job.data ?? null}
          jobError={job.error ?? rerun.error}
          routerBlocks={plan.data?.routerBlocks ?? []}
          rerunning={rerun.isPending}
          onRerun={() => onboarded && rerun.mutate(onboarded)}
          onOpen={() => onboarded && navigate(`/olts/${onboarded.number ?? onboarded.id}`)}
        />
      )}

      <div className="mt-6 flex items-center justify-between">
        <Button
          onClick={() => setStepIndex(Math.max(0, stepIndex - 1))}
          disabled={stepIndex === 0 || locked}
          icon={<ArrowLeft className="size-4" />}
        >
          Back
        </Button>

        {step.id === 'setup' ? (
          onboarded ? null : (
            <Button variant="primary" loading={onboard.isPending} onClick={() => onboard.mutate()}>
              Onboard and set up
            </Button>
          )
        ) : (
          <Button
            variant="primary"
            onClick={goNext}
            disabled={
              (step.id === 'platform' && !canLeavePlatform) ||
              (step.id === 'network' && !canLeaveNetwork) ||
              (step.id === 'verify' && !preflight.data?.ready)
            }
            icon={<ArrowRight className="size-4" />}
          >
            Continue
          </Button>
        )}
      </div>
    </>
  );
}

// --- Addressing: credentials and VLANs ---------------------------------------------

function CopyButton({ text, label = 'Copy' }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      size="sm"
      variant="ghost"
      icon={<Copy className="size-3.5" />}
      onClick={async () => {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 1200);
      }}
      aria-label={label}
    >
      {copied ? 'Copied' : null}
    </Button>
  );
}

function RegenerateButton({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <Button size="sm" variant="ghost" icon={<RefreshCw className="size-3.5" />} onClick={onClick} aria-label={label} title={label} />
  );
}

function CredentialsCard({
  state,
  set,
}: {
  state: WizardState;
  set: (patch: Partial<WizardState>) => void;
}) {
  const communities = state.snmpVersion !== '3';
  return (
    <Card title="Credentials this platform will use">
      <div className="grid gap-4 p-5 md:grid-cols-2 xl:grid-cols-3">
        <Field label="SNMP version">
          <select
            className="input"
            value={state.snmpVersion}
            onChange={(e) => set({ snmpVersion: e.target.value as WizardState['snmpVersion'] })}
          >
            <option value="2c">v2c</option>
            <option value="1">v1</option>
            <option value="3">v3</option>
          </select>
        </Field>
        <Field
          label="SNMP read community"
          hint={communities ? 'Generated. SimpleACS puts it on the chassis during setup.' : 'SNMP v3 users are made on the chassis by hand.'}
        >
          <div className="flex gap-1">
            <Input
              value={state.snmpCommunity}
              onChange={(e) => set({ snmpCommunity: e.target.value })}
              className="font-mono"
              autoComplete="off"
            />
            <RegenerateButton label="Generate another read community" onClick={() => set({ snmpCommunity: generateOltCommunity('ro') })} />
          </div>
        </Field>
        <Field
          label="SNMP write community"
          hint="Generated. The platform writes over SNMP too — ONU names, admin state, reboots."
        >
          <div className="flex gap-1">
            <Input
              value={state.snmpCommunityRw}
              onChange={(e) => set({ snmpCommunityRw: e.target.value })}
              className="font-mono"
              autoComplete="off"
            />
            <RegenerateButton label="Generate another write community" onClick={() => set({ snmpCommunityRw: generateOltCommunity('rw') })} />
          </div>
        </Field>
        <Field label="CLI transport">
          <select
            className="input"
            value={state.cliTransport}
            onChange={(e) =>
              set({
                cliTransport: e.target.value as WizardState['cliTransport'],
                cliPort: e.target.value === 'ssh' ? '22' : '23',
              })
            }
          >
            <option value="telnet">Telnet</option>
            <option value="ssh">SSH</option>
          </select>
        </Field>
        <Field label="CLI username">
          <Input
            value={state.cliUsername}
            onChange={(e) => set({ cliUsername: e.target.value })}
            autoComplete="off"
          />
        </Field>
        {/* Shown, not masked: the operator types it into the chassis on the
            next step, exactly as it is here. */}
        <Field label="CLI password" hint="Generated. You type it on the chassis in the Prepare step.">
          <div className="flex gap-1">
            <Input
              value={state.cliPassword}
              onChange={(e) => set({ cliPassword: e.target.value })}
              className="font-mono"
              autoComplete="off"
              spellCheck={false}
            />
            <CopyButton text={state.cliPassword} label="Copy the password" />
            <RegenerateButton label="Generate another password" onClick={() => set({ cliPassword: generateOltPassword() })} />
          </div>
        </Field>
        <Field label="SNMP port">
          <Input value={state.snmpPort} onChange={(e) => set({ snmpPort: e.target.value })} />
        </Field>
        <Field label="CLI port">
          <Input value={state.cliPort} onChange={(e) => set({ cliPort: e.target.value })} />
        </Field>
      </div>
    </Card>
  );
}

function VlansCard({
  rows,
  problems,
  gatewayName,
  onChange,
}: {
  rows: VlanDraft[];
  problems: Map<string, string>;
  gatewayName: string | null;
  onChange: (rows: VlanDraft[]) => void;
}) {
  const update = (key: string, patch: Partial<VlanDraft>) =>
    onChange(rows.map((row) => (row.key === key ? { ...row, ...patch } : row)));

  return (
    <Card
      title="VLANs"
      action={
        <Button size="sm" icon={<Plus className="size-3.5" />} onClick={() => onChange([...rows, newVlanDraft()])}>
          Add VLAN
        </Button>
      }
    >
      <div className="space-y-3 p-5">
        <p className="text-sm text-muted">
          SimpleACS creates these on the OLT during setup and leaves any it already carries alone.
          Tick <span className="font-medium text-ink">Management</span> for a VLAN the ONUs’ own
          TR-069 traffic uses — it needs the subnet those ONUs take their addresses from, and{' '}
          {gatewayName ? `${gatewayName} is given it` : 'the gateway is given it'} if it does not
          route it yet. Other VLANs need no subnet.
        </p>
        <div className="hidden grid-cols-[6rem_1fr_7rem_12rem_2.25rem] gap-2 px-1 text-xs text-muted md:grid">
          <span>VLAN</span>
          <span>Name</span>
          <span>Role</span>
          <span>Subnet</span>
          <span />
        </div>
        {rows.map((row) => {
          const problem = problems.get(row.key);
          return (
            <div key={row.key} className="space-y-1">
              <div className="grid grid-cols-2 gap-2 md:grid-cols-[6rem_1fr_7rem_12rem_2.25rem] md:items-center">
                <Input
                  value={row.vlanId}
                  onChange={(e) => update(row.key, { vlanId: e.target.value })}
                  placeholder="10"
                  inputMode="numeric"
                  aria-label="VLAN id"
                />
                <Input
                  value={row.name}
                  onChange={(e) => update(row.key, { name: e.target.value })}
                  placeholder="onu mngt"
                  aria-label="VLAN name"
                />
                <label className="flex items-center gap-2 text-sm text-ink">
                  <input
                    type="checkbox"
                    className="size-4 accent-primary"
                    checked={row.management}
                    onChange={(e) => update(row.key, { management: e.target.checked })}
                  />
                  Management
                </label>
                <Input
                  value={row.subnet}
                  onChange={(e) => update(row.key, { subnet: e.target.value })}
                  placeholder={row.management ? '10.10.0.0/16' : 'not needed'}
                  disabled={!row.management}
                  aria-label="Management subnet"
                  className="font-mono"
                />
                <Button
                  size="sm"
                  variant="ghost"
                  className="justify-self-end text-danger"
                  icon={<X className="size-4" />}
                  aria-label="Remove this VLAN"
                  onClick={() => onChange(rows.filter((r) => r.key !== row.key))}
                />
              </div>
              {problem && !isEmptyDraft(row) && <p className="px-1 text-xs text-danger">{problem}</p>}
            </div>
          );
        })}
        {rows.length === 0 && (
          <p className="text-sm text-faint">No VLANs — the setup leaves the chassis’s VLANs as they are.</p>
        )}
      </div>
    </Card>
  );
}

// --- Prepare and Verify ------------------------------------------------------------

function CopyBlock({ block }: { block: CommandBlock }) {
  const [copied, setCopied] = useState(false);
  const text = useMemo(() => block.commands.join('\n'), [block.commands]);

  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          {block.title}
          <Pill tone={block.target === 'olt' ? 'primary' : 'info'}>
            {block.target === 'olt' ? 'on the OLT' : 'on the site router'}
          </Pill>
          {block.verifyOnHardware && <Pill tone="warn">verify syntax</Pill>}
        </span>
      }
      action={
        <Button
          size="sm"
          icon={<Copy className="size-3.5" />}
          onClick={async () => {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            setTimeout(() => setCopied(false), 1200);
          }}
        >
          {copied ? 'Copied' : 'Copy'}
        </Button>
      }
    >
      <div className="space-y-3 p-5">
        <p className="text-sm text-muted">{block.rationale}</p>
        <pre className="overflow-x-auto rounded-lg border border-line bg-surface-2 p-4 font-mono text-xs leading-relaxed text-ink">
          {text}
        </pre>
        {block.notes.length > 0 && (
          <ul className="space-y-1">
            {block.notes.map((note) => (
              <li key={note} className="text-xs text-faint">
                — {note}
              </li>
            ))}
          </ul>
        )}
      </div>
    </Card>
  );
}

function PrepareStep({
  plan,
  error,
  loading,
}: {
  plan: PrepPlan | undefined;
  error: unknown;
  loading: boolean;
}) {
  if (loading) {
    return (
      <div className="flex justify-center p-12">
        <Spinner className="size-6" />
      </div>
    );
  }
  if (error) return <ErrorNotice error={error} />;
  if (!plan) return null;

  return (
    <div className="space-y-5">
      <Card title="Getting to the console">
        <div className="space-y-2 p-5">
          <p className="text-sm text-ink">{plan.console.method}</p>
          <div className="flex flex-wrap gap-4 text-sm text-muted">
            {plan.console.defaultAddress && (
              <span>
                Factory address: <code className="font-mono">{plan.console.defaultAddress}</code>
              </span>
            )}
            {plan.console.defaultCredentials && (
              <span>
                Factory login: <code className="font-mono">{plan.console.defaultCredentials}</code>
              </span>
            )}
          </div>
          <ul className="space-y-1 pt-1">
            {plan.console.notes.map((note) => (
              <li key={note} className="text-xs text-faint">
                — {note}
              </li>
            ))}
          </ul>
        </div>
      </Card>

      {plan.blocks.map((block) => (
        <CopyBlock key={block.id} block={block} />
      ))}

      <Card title="What happens when you continue">
        <div className="grid gap-5 p-5 md:grid-cols-2">
          <div>
            <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted">Checked first</p>
            <ul className="space-y-2">
              {plan.willVerify.map((item) => (
                <li key={item} className="flex items-start gap-2 text-sm text-muted">
                  <Radar className="mt-0.5 size-4 shrink-0 text-primary" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted">
              Then set up by SimpleACS
            </p>
            <ul className="space-y-2">
              {plan.willSetUp.map((item) => (
                <li key={item} className="flex items-start gap-2 text-sm text-muted">
                  <Check className="mt-0.5 size-4 shrink-0 text-success" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Card>
    </div>
  );
}

const CHECK_ICON = {
  pass: <Check className="size-4 text-success" />,
  warn: <AlertTriangle className="size-4 text-warn" />,
  fail: <X className="size-4 text-danger" />,
  skipped: <MinusCircle className="size-4 text-faint" />,
};

function VerifyStep({
  result,
  error,
  loading,
  onRetry,
}: {
  result: { ready: boolean; checks: PreflightCheck[]; detectedModel: string | null } | undefined;
  error: unknown;
  loading: boolean;
  onRetry: () => void;
}) {
  if (loading) {
    return (
      <Card>
        <div className="flex flex-col items-center gap-3 p-12">
          <CircleDashed className="size-6 animate-spin text-primary" />
          <p className="text-sm text-muted">Logging in to the OLT…</p>
        </div>
      </Card>
    );
  }
  if (error) return <ErrorNotice error={error} />;
  if (!result) return null;

  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          Verification
          <Pill state tone={result.ready ? 'success' : 'danger'} dot>
            {result.ready ? 'ready to set up' : 'not ready'}
          </Pill>
        </span>
      }
      action={
        <Button size="sm" icon={<Radar className="size-3.5" />} onClick={onRetry}>
          Run again
        </Button>
      }
    >
      <ul className="divide-y divide-line">
        {result.checks.map((check) => (
          <li key={check.id} className="flex items-start gap-3 px-5 py-3.5">
            <span className="mt-0.5">{CHECK_ICON[check.status]}</span>
            <div className="min-w-0 flex-1">
              <div className="text-sm text-ink">{check.label}</div>
              <div className="text-sm text-muted">{check.detail}</div>
              {check.remedy && <div className="mt-1 text-xs text-warn">{check.remedy}</div>}
            </div>
          </li>
        ))}
      </ul>
    </Card>
  );
}

// --- Set up ------------------------------------------------------------------------

const STEP_LABELS: Record<OltSetupStepId, string> = {
  snmp: 'SNMP read and write communities',
  ntp: 'Time servers',
  sweep: 'First inventory sweep',
  vlans: 'VLANs',
  gateway: 'Gateway routes the management subnets',
};

const STATUS_PILL: Record<OltSetupStep['status'], { tone: 'success' | 'neutral' | 'danger' | 'info'; text: string }> = {
  done: { tone: 'success', text: 'done' },
  already: { tone: 'neutral', text: 'already there' },
  skipped: { tone: 'neutral', text: 'skipped' },
  failed: { tone: 'danger', text: 'failed' },
  planned: { tone: 'info', text: 'would do' },
};

function StepIcon({ status }: { status: OltSetupStep['status'] | 'running' | 'waiting' }) {
  if (status === 'running') return <Loader2 className="size-4 animate-spin text-primary" />;
  if (status === 'waiting') return <CircleDashed className="size-4 text-faint" />;
  if (status === 'failed') return <X className="size-4 text-danger" />;
  if (status === 'skipped') return <MinusCircle className="size-4 text-faint" />;
  if (status === 'already') return <Check className="size-4 text-muted" />;
  return <Check className="size-4 text-success" />;
}

function SetupStep({
  state,
  gatewayName,
  detectedModel,
  onboarded,
  onboardError,
  job,
  jobError,
  routerBlocks,
  rerunning,
  onRerun,
  onOpen,
}: {
  state: WizardState;
  gatewayName: string | null;
  detectedModel: string | null;
  onboarded: Olt | null;
  onboardError: unknown;
  job: JobStatus | null;
  jobError: unknown;
  routerBlocks: CommandBlock[];
  rerunning: boolean;
  onRerun: () => void;
  onOpen: () => void;
}) {
  const vlans = toOnboardingVlans(state.vlans);

  if (!onboarded) {
    return (
      <Card title="Ready to onboard">
        <div className="space-y-4 p-5">
          <dl className="grid gap-3 sm:grid-cols-2">
            {[
              ['Name', state.name],
              ['Platform', OLT_PLATFORM_LABELS[state.platform]],
              ['Management address', `${state.mgmtIp} (${state.cliTransport} ${state.cliPort})`],
              ['Reached through', gatewayName ?? 'Directly routable'],
              ['Detected', detectedModel ?? 'confirmed once SNMP answers'],
              ['Time servers', parseNtpServers(state.ntpServers).join(', ') || 'the platform’s, from Settings'],
              [
                'VLANs',
                vlans.length
                  ? vlans.map((v) => `${v.vlanId}${v.name ? ` ${v.name}` : ''}${v.management ? ` (management, ${v.subnet})` : ''}`).join(' · ')
                  : 'none — left as they are',
              ],
              ['Timezone', state.timezone ? `${state.timezone} (recorded, not written)` : 'none'],
              ['Event notifications', state.eventNotifications ? 'shown on the OLT list' : 'hidden on the OLT list (events still recorded)'],
            ].map(([label, value]) => (
              <div key={label} className="rounded-lg border border-line bg-surface-2 px-4 py-3">
                <dt className="text-xs text-muted">{label}</dt>
                <dd className="mt-0.5 text-sm text-ink">{value}</dd>
              </div>
            ))}
          </dl>
          <p className="text-sm text-muted">
            Onboarding registers the OLT, then SimpleACS logs in and puts SNMP, the time servers and
            the VLANs on the chassis, sweeps it, and makes sure the gateway routes the management
            subnets — skipping anything already there. A step the chassis refuses does not undo
            the rest; it can be run again from here.
          </p>
          {onboardError ? <ErrorNotice error={onboardError} /> : null}
        </div>
      </Card>
    );
  }

  const running = !job || job.status === 'running' || job.status === 'queued';
  const result = job?.status === 'done' ? (job.result as OltSetupResult) : null;
  const current = running && job?.progress != null ? Math.floor((job.progress / 100) * OLT_SETUP_STEPS.length) : 0;
  const failed = result?.steps.some((s) => s.status === 'failed') ?? false;

  return (
    <div className="space-y-5">
      <Card
        title={
          <span className="flex flex-wrap items-center gap-2">
            Setting up {onboarded.name}
            {running ? (
              <Pill tone="info" dot>
                working
              </Pill>
            ) : result ? (
              <Pill state tone={failed ? 'warn' : 'success'} dot>
                {failed ? 'finished with a refusal' : 'set up'}
              </Pill>
            ) : null}
          </span>
        }
        action={
          !running ? (
            <div className="flex gap-2">
              <Button size="sm" icon={<RefreshCw className="size-3.5" />} loading={rerunning} onClick={onRerun}>
                Run setup again
              </Button>
              <Button size="sm" variant="primary" onClick={onOpen}>
                Open the OLT
              </Button>
            </div>
          ) : null
        }
      >
        <ul className="divide-y divide-line">
          {OLT_SETUP_STEPS.map((id, index) => {
            const done = result?.steps.find((s) => s.id === id);
            const status = done ? done.status : !running ? 'waiting' : index < current ? 'already' : index === current ? 'running' : 'waiting';
            return (
              <li key={id} className="flex items-start gap-3 px-5 py-3.5">
                <span className="mt-0.5">
                  <StepIcon status={done ? done.status : status === 'already' ? 'done' : status} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2 text-sm text-ink">
                    {STEP_LABELS[id]}
                    {done && <Pill state tone={STATUS_PILL[done.status].tone}>{STATUS_PILL[done.status].text}</Pill>}
                    {done?.unverified && (
                      <Pill tone="warn" title="The platform has not yet run this on a chassis of this family — built from captured grammar, awaiting one to prove it on.">
                        not yet run on this family
                      </Pill>
                    )}
                  </div>
                  {done ? (
                    <div className="text-sm text-muted">{done.detail}</div>
                  ) : index === current && running && job?.message ? (
                    <div className="text-sm text-muted">{job.message}</div>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
        {job?.status === 'failed' && (
          <div className="px-5 pb-4">
            <ErrorNotice error={job.message ?? 'The setup stopped.'} />
          </div>
        )}
        {jobError ? (
          <div className="px-5 pb-4">
            <ErrorNotice error={jobError} />
          </div>
        ) : null}
      </Card>

      {routerBlocks.length > 0 && (
        <>
          <p className="text-sm text-muted">
            The site router’s half of each management VLAN — its gateway address and DHCP. Neither
            the OLT’s nor SimpleACS’s to apply.
          </p>
          {routerBlocks.map((block) => (
            <CopyBlock key={block.id} block={block} />
          ))}
        </>
      )}
    </div>
  );
}
